/**
 * src/features/learning/components/AdaptiveEdgeSummaryView.tsx
 *
 * MER Algo Footprints v7.1H — Adaptive Edge Summary View (v1 Basic Stats)
 *
 * Displays basic win/loss/win_rate statistics by setup type, regime, and symbol.
 * No recommendations, no calibration, no false positive analysis in v1.
 */

import React, { useEffect } from 'react';
import { useLearningData, GroupStats, AdaptiveEdgeSummary } from '../hooks/useLearningData';

const mono: React.CSSProperties = { fontFamily: 'var(--mono)' };

function SectionHead({ icon, title }: { icon: string; title: string }) {
  return (
    <div style={{
      ...mono, fontSize: 9, color: 'var(--gold)',
      textTransform: 'uppercase' as const, letterSpacing: '1px',
      marginBottom: 8, marginTop: 14,
      borderBottom: '1px solid var(--border)', paddingBottom: 4,
    }}>
      {icon} {title}
    </div>
  );
}

function winRateColor(wr: number | null): string {
  if (wr === null) return 'var(--blue-dim)';
  if (wr >= 0.60)  return 'var(--green)';
  if (wr >= 0.45)  return 'var(--cyan)';
  if (wr >= 0.35)  return 'var(--amber)';
  return 'var(--red)';
}

function StatsTable({ data, keyLabel }: { data: Record<string, GroupStats>; keyLabel: string }) {
  const rows = Object.entries(data).sort(([, a], [, b]) => b.total - a.total);
  if (!rows.length) {
    return <div style={{ ...mono, fontSize: 10, color: 'var(--blue-dim)' }}>No data yet.</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, ...mono }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--border)' }}>
          {[keyLabel, 'Total', 'W', 'L', 'Win%'].map(h => (
            <th key={h} style={{
              padding: '2px 8px', textAlign: h === keyLabel ? 'left' : 'right',
              fontWeight: 400, fontSize: 9, color: 'var(--blue-dim)',
              textTransform: 'uppercase' as const, letterSpacing: '.5px',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([key, s]) => (
          <tr key={key} style={{ borderBottom: '1px solid var(--border-faint, var(--border))' }}>
            <td style={{ padding: '3px 8px', color: 'var(--cyan)' }}>{key.replace(/_/g, ' ')}</td>
            <td style={{ padding: '3px 8px', textAlign: 'right' }}>{s.total}</td>
            <td style={{ padding: '3px 8px', textAlign: 'right', color: 'var(--green)' }}>{s.wins}</td>
            <td style={{ padding: '3px 8px', textAlign: 'right', color: 'var(--red)' }}>{s.losses}</td>
            <td style={{ padding: '3px 8px', textAlign: 'right', color: winRateColor(s.win_rate) }}>
              {s.win_rate != null ? `${(s.win_rate * 100).toFixed(0)}%` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OverallBar({ s }: { s: AdaptiveEdgeSummary }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
      {[
        { label: 'Analyzed',  value: String(s.total_analyzed) },
        { label: 'Completed', value: String(s.total_completed) },
        { label: 'Wins',      value: String(s.overall.wins) },
        { label: 'Win Rate',  value: s.overall.win_rate != null ? `${(s.overall.win_rate * 100).toFixed(0)}%` : '—',
          color: winRateColor(s.overall.win_rate) },
      ].map(({ label, value, color }) => (
        <div key={label} style={{ textAlign: 'center' }}>
          <div style={{ ...mono, fontSize: 18, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
          <div style={{ ...mono, fontSize: 9, color: 'var(--blue-dim)', textTransform: 'uppercase' as const }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

export function AdaptiveEdgeSummaryView() {
  const { edgeSummary, edgeLoading, edgeError, loadEdge } = useLearningData();

  useEffect(() => { loadEdge(); }, [loadEdge]);

  const btnBase: React.CSSProperties = {
    ...mono, fontSize: 10, padding: '3px 10px', borderRadius: 3,
    cursor: 'pointer', border: '1px solid var(--border)',
    background: 'var(--surface2)', color: 'var(--blue-dim)',
  };

  if (edgeLoading) return <div style={{ ...mono, fontSize: 11, color: 'var(--blue-dim)', padding: 16 }}>Computing…</div>;
  if (edgeError)   return <div style={{ ...mono, fontSize: 11, color: 'var(--red)', padding: 16 }}>Error: {edgeError}</div>;
  if (!edgeSummary) return <div style={{ ...mono, fontSize: 11, color: 'var(--blue-dim)', padding: 16 }}>No data yet.</div>;

  const s = edgeSummary;

  return (
    <div style={{ ...mono, fontSize: 11, color: 'var(--text)', padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 9, color: 'var(--blue-dim)' }}>
          {new Date(s.computed_at).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET
          {s.note && <span style={{ marginLeft: 8, color: 'var(--amber)' }}>{s.note}</span>}
        </div>
        <button onClick={() => loadEdge(true)} style={btnBase}>↻ Refresh</button>
      </div>

      <OverallBar s={s} />

      <SectionHead icon="📈" title="By Setup Type" />
      <StatsTable data={s.setup_stats} keyLabel="Setup" />

      <SectionHead icon="🌊" title="By Regime" />
      <StatsTable data={s.regime_stats} keyLabel="Regime" />

      <SectionHead icon="◎" title="By Symbol" />
      <StatsTable data={s.symbol_stats} keyLabel="Symbol" />
    </div>
  );
}

export default AdaptiveEdgeSummaryView;
